import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { AdjustStockModal } from './AdjustStockModal.jsx';
import { NewItemModal } from './NewItemModal.jsx';

const YELLOW = '#F4C430';
const TEAL = '#5DD0C8';

const STATUS_META = {
  low:    { label: 'Low',           pill: 'bg-ink-magenta/[0.12] border-ink-magenta/[0.3] text-ink-magenta', dot: 'bg-ink-magenta', dotRing: 'shadow-[0_0_0_3px_rgba(230,59,122,0.15)]' },
  medium: { label: 'Medium',        pill: 'bg-ink-yellow/[0.12] border-ink-yellow/[0.3] text-ink-yellow',  dot: 'bg-ink-yellow' },
  high:   { label: 'High',          pill: 'bg-status-ok/[0.12] border-status-ok/[0.3] text-status-ok',  dot: 'bg-status-ok' },
  unset:  { label: 'Unset',         pill: 'border-border-strong text-text-tertiary',                                    dot: 'bg-status-info' },
};

const STATUS_ORDER = { low: 0, unset: 1, medium: 2, high: 3 };
const CATEGORY_LABEL = { ink: 'Inks', media: 'Media', maintenance: 'Maintenance', parts: 'Parts', global: 'Global' };

// ─── Threshold input — controlled, save on blur ───────────────────────────

const ThresholdInput = ({ value, onSave, label }) => {
  const [local, setLocal] = useState(value == null ? '' : String(value));
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState(null);

  useEffect(() => { setLocal(value == null ? '' : String(value)); }, [value]);

  // Auto-clear the saved tick after 1.5s.
  useEffect(() => {
    if (status !== 'saved') return;
    const t = setTimeout(() => setStatus('idle'), 1500);
    return () => clearTimeout(t);
  }, [status]);

  const commit = async () => {
    const next = local.trim() === '' ? null : parseInt(local, 10);
    if (next === value) { setStatus('idle'); return; }
    if (next != null && (!Number.isFinite(next) || next < 0)) {
      setLocal(value == null ? '' : String(value));
      return;
    }
    setStatus('saving');
    setError(null);
    try {
      await onSave(next);
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(err.message ?? 'Failed to save');
      setLocal(value == null ? '' : String(value));
    }
  };

  const ring = {
    saving: 'border-ink-yellow shadow-[0_0_0_1px_#F4C430]',
    saved:  'border-status-ok shadow-[0_0_0_1px_#7BD4A8]',
    error:  'border-ink-magenta shadow-[0_0_0_1px_#E63B7A]',
    idle:   'border-border-strong',
  }[status];

  return (
    <div class="flex flex-col items-end gap-0.5">
      <div class="flex items-center gap-1.5">
        <span class="text-[9px] font-bold uppercase tracking-[0.08em] text-text-muted">{label}</span>
        {status === 'saving' && <span class="text-[10px] font-bold text-ink-yellow">saving…</span>}
        {status === 'saved'  && <span class="text-[10px] font-bold text-status-ok">saved ✓</span>}
        {status === 'error'  && <span class="text-[10px] font-bold text-ink-magenta" title={error ?? ''}>!</span>}
      </div>
      <input
        type="number"
        min={0}
        value={local}
        placeholder="—"
        disabled={status === 'saving'}
        onInput={(e) => { setLocal(e.currentTarget.value); if (status !== 'idle') setStatus('idle'); }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        class={`w-[72px] rounded border bg-bg-base px-2 py-1 text-right font-serif text-[15px] font-medium text-text-primary placeholder:text-text-muted focus:outline-none transition ${ring} ${status === 'saving' ? 'opacity-70' : ''}`}
      />
      {status === 'error' && (
        <span class="max-w-[160px] text-right text-[10px] font-medium leading-tight text-ink-magenta">
          {error}
        </span>
      )}
    </div>
  );
};

// ─── Item row ──────────────────────────────────────────────────────────────

const ItemRow = ({ item, editing, showPrinter, onAdjust, onOpen, onUpdated }) => {
  const status = STATUS_META[item.stockStatus] ?? STATUS_META.unset;
  const [busy, setBusy] = useState(false);

  const patchThreshold = async (field, value) => {
    const updated = await api.patch(`/inventory/${item.id}`, { [field]: value });
    onUpdated?.(updated);
  };

  const quickAdjust = (delta) => async () => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await api.post(`/inventory/${item.id}/adjust`, { changeAmount: delta });
      onUpdated?.(updated);
    } catch (err) {
      alert(err.message ?? 'Failed to adjust');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class={`grid items-center gap-2.5 border-b border-border-subtle px-4 py-2 last:border-b-0 hover:bg-[rgba(255,255,255,0.02)] ${
        editing
          ? 'grid-cols-[28px_minmax(0,1fr)_auto_auto_auto_auto] pr-4'
          : 'grid-cols-[28px_minmax(0,1fr)_auto_auto_auto] pr-4'
      }`}
    >
      <span class={`block h-2 w-2 rounded-full ml-2 ${status.dot} ${status.dotRing ?? ''}`} aria-hidden="true" />

      <button onClick={() => onOpen(item.id)} class="min-w-0 cursor-pointer text-left focus:outline-none focus-visible:underline">
        <p class="m-0 flex flex-wrap items-center gap-1.5 truncate text-[14px] font-semibold text-text-primary">
          {item.name}
          {showPrinter && (
            <span class="inline-flex items-center gap-1 rounded-md border border-border-strong bg-bg-base px-1.5 py-px text-[10px] font-semibold text-text-tertiary">
              {item.linkedPrinters.length === 0
                ? 'Global'
                : item.linkedPrinters.map((p) => p.name).join(' + ')}
            </span>
          )}
          {!showPrinter && (item.linkedPrinters?.length ?? 0) > 1 && (
            <span
              title={`Shared stock — linked to ${item.linkedPrinters.map((p) => p.name).join(', ')}`}
              class="inline-flex items-center gap-0.5 rounded-full border border-ink-yellow/[0.3] bg-ink-yellow/[0.1] px-1.5 py-0 text-[9px] font-bold uppercase tracking-[0.04em] text-ink-yellow"
            >
              🔗 shared · {item.linkedPrinters.length}
            </span>
          )}
        </p>
        <p class="m-0 text-[11px] font-medium text-text-tertiary">
          <span class="capitalize">{item.category}</span>
          {item.productId && <span class="ml-1.5 font-mono text-text-muted">· {item.productId}</span>}
          {item.brandName && <span class="text-text-muted"> · {item.brandName}</span>}
        </p>
      </button>

      <span class={`inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] ${status.pill}`}>
        {status.label}
      </span>

      {editing ? (
        <>
          <p class={`m-0 min-w-[44px] text-right font-serif text-[22px] font-medium tracking-tighter ${item.stockCount === 0 ? 'text-ink-magenta' : 'text-text-primary'}`}>
            {item.stockCount}
          </p>
          <ThresholdInput value={item.lowThreshold} label="Low ≤" onSave={(v) => patchThreshold('lowThreshold', v)} />
          <ThresholdInput value={item.medThreshold} label="Med ≤" onSave={(v) => patchThreshold('medThreshold', v)} />
        </>
      ) : (
        <>
          <div class="inline-flex items-center gap-1.5">
            <button
              type="button"
              onClick={quickAdjust(-1)}
              disabled={busy || item.stockCount === 0}
              title="Consume one"
              aria-label="Consume one"
              class="flex h-7 w-7 items-center justify-center rounded-md border border-border-strong bg-transparent font-semibold text-text-body hover:bg-bg-base disabled:cursor-not-allowed disabled:opacity-30"
            >
              −
            </button>
            <p class={`m-0 min-w-[36px] text-center font-serif text-[22px] font-medium tracking-tighter ${item.stockCount === 0 ? 'text-ink-magenta' : 'text-text-primary'} ${busy ? 'opacity-50' : ''}`}>
              {item.stockCount}
            </p>
            <button
              type="button"
              onClick={quickAdjust(+1)}
              disabled={busy}
              title="Restock one"
              aria-label="Restock one"
              class="flex h-7 w-7 items-center justify-center rounded-md border border-border-strong bg-transparent font-semibold text-text-body hover:bg-bg-base disabled:cursor-not-allowed disabled:opacity-30"
            >
              +
            </button>
          </div>
          <button
            type="button"
            onClick={() => onAdjust(item)}
            class="whitespace-nowrap rounded-md border border-border-strong bg-transparent px-3 py-1 text-[12px] font-semibold text-text-body hover:bg-bg-base"
          >
            Detailed adjust
          </button>
        </>
      )}
    </div>
  );
};

// ─── Section card ──────────────────────────────────────────────────────────

const SECTION_ICON = {
  proofer:  'bg-gradient-to-br from-[#2A6660] to-[#1A3D38] text-ink-teal',
  global:   'bg-gradient-to-br from-[#5C4E2A] to-[#3A2E14] text-ink-yellow',
  category: 'bg-gradient-to-br from-[#2C475A] to-[#142434] text-status-info',
};

const SectionCard = ({
  kind,                // 'proofer' | 'global' | 'category'
  initial,             // single letter for the corner mark
  title,
  subtitle,
  items,
  editing,
  isAdmin,
  defaultOpen = false,
  onAdjust,
  onOpen,
  onItemUpdated,
  onAddItem,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const stats = useMemo(() => {
    const acc = { total: 0, low: 0, medium: 0, high: 0, unset: 0 };
    for (const it of items) { acc.total += 1; acc[it.stockStatus] += 1; }
    return acc;
  }, [items]);

  const sorted = useMemo(
    () => [...items].sort(
      (a, b) => (STATUS_ORDER[a.stockStatus] - STATUS_ORDER[b.stockStatus]) || a.name.localeCompare(b.name),
    ),
    [items],
  );

  return (
    <div class="mb-3 overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
      <div
        class="flex w-full items-center gap-3.5 border-b border-border-subtle bg-bg-header px-4 py-3 hover:bg-bg-hover"
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          class="flex flex-1 items-center gap-3.5 text-left focus:outline-none"
        >
          <div class={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] font-serif text-[18px] font-medium ${SECTION_ICON[kind]}`}>
            {initial}
          </div>
          <div class="min-w-0 flex-1">
            <p class="m-0 truncate font-serif text-[19px] font-medium tracking-tight text-text-primary">{title}</p>
            {subtitle && <p class="m-0 mt-[1px] truncate text-[12px] font-medium text-text-tertiary">{subtitle}</p>}
          </div>
          <div class="flex items-center gap-1.5">
            <StatPill tone="total" value={stats.total} label="items" />
            {stats.low > 0 && <StatPill tone="low" value={stats.low} label="low" />}
            {stats.unset > 0 && <StatPill tone="unset" value={stats.unset} label="unset" />}
          </div>
        </button>
        {isAdmin && onAddItem && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAddItem(); }}
            title={`Add item to ${title}`}
            class="rounded-md border border-border-strong px-2 py-1 text-[11px] font-semibold text-text-tertiary hover:border-ink-yellow hover:text-ink-yellow"
          >
            + Add
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          class={`text-text-muted transition-transform ${open ? '' : '-rotate-90'}`}
        >
          ▾
        </button>
      </div>

      {open && (
        <div>
          {sorted.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              editing={editing}
              showPrinter={kind === 'category'}
              onAdjust={onAdjust}
              onOpen={onOpen}
              onUpdated={onItemUpdated}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StatPill = ({ tone, value, label }) => {
  const map = {
    total: 'border-border-strong text-text-tertiary',
    low:   'bg-ink-magenta/[0.12] border-ink-magenta/[0.3] text-ink-magenta',
    unset: 'bg-ink-teal/[0.12] border-ink-teal/[0.3] text-ink-teal',
  };
  return (
    <span class={`inline-flex items-center gap-1 rounded-full border px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.04em] ${map[tone]}`}>
      <span class="font-serif text-[13px] font-medium">{value}</span>{label}
    </span>
  );
};

// ─── Top stats tile ────────────────────────────────────────────────────────

const Stat = ({ label, value, accent }) => (
  <div class="relative overflow-hidden rounded-sm border border-border-subtle bg-bg-card p-3">
    <span class="absolute inset-y-0 left-0 w-[3px]" style={{ background: accent }} aria-hidden="true" />
    <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">{label}</p>
    <p class="m-0 mt-1 font-serif text-[24px] font-medium tracking-tighter text-text-primary">{value}</p>
  </div>
);

// ─── Page ──────────────────────────────────────────────────────────────────

export const InventoryIndexPage = () => {
  const { route } = useLocation();
  const { data: items, loading, error, refetch } = useApi('/inventory?status=active');
  const { data: stats, refetch: refetchStats } = useApi('/inventory/stats');
  const { data: printers } = useApi('/printers');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [view, setView] = useState('byPrinter');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editingThresholds, setEditingThresholds] = useState(false);
  const [adjusting, setAdjusting] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newPrefill, setNewPrefill] = useState({});
  const [localItems, setLocalItems] = useState(null);

  const openAdd = (prefill = {}) => {
    setNewPrefill(prefill);
    setNewOpen(true);
  };

  // Whenever the server refetches, sync local items
  useEffect(() => { setLocalItems(items ?? null); }, [items]);

  const displayItems = localItems ?? items ?? [];

  const visible = useMemo(() => {
    let result = displayItems;
    if (statusFilter !== 'all') result = result.filter((i) => i.stockStatus === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((i) => i.name.toLowerCase().includes(q) || (i.printerName ?? '').toLowerCase().includes(q));
    }
    return result;
  }, [displayItems, statusFilter, search]);

  const printersById = useMemo(() => Object.fromEntries((printers ?? []).map((p) => [p.id, p])), [printers]);

  const groupedByPrinter = useMemo(() => {
    const map = new Map();
    map.set('global', { key: 'global', kind: 'global', initial: 'G', title: 'Global', subtitle: 'Shared across the proofing room', items: [] });
    (printers ?? []).forEach((p) => {
      const sub = [p.model ?? '', p.ipAddress ?? '', p.limitations ? `⚠ ${p.limitations}` : '']
        .filter(Boolean).join(' · ');
      map.set(`p${p.id}`, {
        key: `p${p.id}`,
        kind: 'proofer',
        initial: p.name.charAt(0).toUpperCase(),
        title: p.name,
        subtitle: sub,
        items: [],
      });
    });
    visible.forEach((it) => {
      if (!it.linkedPrinters || it.linkedPrinters.length === 0) {
        map.get('global').items.push(it);
      } else {
        // An item linked to multiple printers appears under each section.
        it.linkedPrinters.forEach((lp) => {
          const key = `p${lp.id}`;
          if (!map.has(key)) {
            map.set(key, { key, kind: 'proofer', initial: lp.name.charAt(0).toUpperCase(), title: lp.name, subtitle: '', items: [] });
          }
          map.get(key).items.push(it);
        });
      }
    });
    return [...map.values()].filter((g) => g.items.length > 0);
  }, [visible, printers]);

  const groupedByCategory = useMemo(() => {
    const buckets = { ink: [], media: [], maintenance: [], parts: [], global: [] };
    visible.forEach((it) => { (buckets[it.category] ??= []).push(it); });
    const printerCount = (arr) => {
      const ids = new Set();
      arr.forEach((i) => (i.linkedPrinters ?? []).forEach((p) => ids.add(p.id)));
      return ids.size;
    };
    return Object.entries(buckets)
      .filter(([_, arr]) => arr.length > 0)
      .map(([cat, arr]) => {
        const n = printerCount(arr);
        return {
          key: cat,
          kind: 'category',
          initial: (CATEGORY_LABEL[cat] ?? cat).charAt(0).toUpperCase(),
          title: CATEGORY_LABEL[cat] ?? cat,
          subtitle: n > 0
            ? `${arr.length} item${arr.length === 1 ? '' : 's'} across ${n} printer${n === 1 ? '' : 's'}`
            : `${arr.length} item${arr.length === 1 ? '' : 's'}`,
          items: arr,
        };
      });
  }, [visible]);

  const handleOpen = (id) => { route(`/inventory/${id}`); };

  const handleItemUpdated = (updated) => {
    setLocalItems((prev) => (prev ?? []).map((i) => (i.id === updated.id ? updated : i)));
    refetchStats();
  };

  return (
    <div>
      <section class="mb-6 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: YELLOW }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: YELLOW }}>Inventory</span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Stock &amp; supplies</h1>
          <p class="m-0 text-sm text-text-secondary">
            Per-printer inks, media, maintenance &amp; parts, plus shared global items. Low &amp; medium thresholds set per item.
          </p>
        </div>
        {isAdmin && (
          <Button variant="secondary" onClick={() => openAdd({})}>+ Add item</Button>
        )}
      </section>

      {stats && (
        <div class="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Total" value={stats.total} accent={YELLOW} />
          <Stat label="Low"   value={stats.low} accent="#E63B7A" />
          <Stat label="Medium" value={stats.medium} accent="#F4C430" />
          <Stat label="Needs thresholds" value={stats.unset} accent={TEAL} />
        </div>
      )}

      {stats?.unset > 0 && (
        <div class="mb-5 flex items-start gap-2.5 rounded-lg border border-ink-teal/[0.3] bg-ink-teal/[0.06] p-3 px-4">
          <span class="text-base font-bold" style={{ color: TEAL }}>•</span>
          <div class="flex-1">
            <p class="m-0 text-[13px] font-semibold" style={{ color: TEAL }}>
              {stats.unset} items need thresholds set
            </p>
            <p class="m-0 text-[12px] font-medium text-text-secondary">
              Toggle "Editing thresholds" above to set them inline, or click an item.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setStatusFilter('unset')}
            class="rounded-md border border-ink-teal/[0.3] px-3 py-1 text-[12px] font-semibold"
            style={{ color: TEAL }}
          >
            Show them
          </button>
        </div>
      )}

      <div class="mb-4 flex flex-wrap items-center gap-3">
        <div class="flex overflow-hidden rounded-md border border-border-strong">
          {[
            { v: 'byPrinter',  label: 'By printer' },
            { v: 'byCategory', label: 'By category' },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setView(o.v)}
              class={`px-3 py-1.5 text-[12px] font-semibold ${view === o.v ? 'text-[#332B00]' : 'text-text-tertiary hover:bg-bg-card'}`}
              style={view === o.v ? { background: YELLOW } : undefined}
            >
              {o.label}
            </button>
          ))}
        </div>

        {[
          { v: 'all',   label: 'All' },
          { v: 'low',   label: 'Low' },
          { v: 'unset', label: 'Unset' },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setStatusFilter(o.v)}
            class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
              statusFilter === o.v
                ? 'border-brand bg-brand text-[#1A0E04]'
                : 'border-border-strong text-text-tertiary hover:bg-bg-card'
            }`}
          >
            {o.label}
          </button>
        ))}

        <input
          type="search"
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search items…"
          class="block w-56 max-w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />

        {isAdmin && (
          <button
            type="button"
            onClick={() => setEditingThresholds((v) => !v)}
            class={`ml-auto inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] font-semibold ${
              editingThresholds
                ? 'border-ink-teal bg-ink-teal/[0.08] text-ink-teal'
                : 'border-border-strong text-text-tertiary hover:bg-bg-card'
            }`}
          >
            <span class="block h-1.5 w-1.5 rounded-full bg-current" />
            {editingThresholds ? 'Editing thresholds' : 'Edit thresholds'}
          </button>
        )}
      </div>

      {editingThresholds && (
        <div class="mb-3 flex items-center gap-2 rounded-md border border-ink-teal/[0.3] bg-ink-teal/[0.06] px-3 py-2 text-[12px] font-medium text-text-secondary">
          <span class="font-semibold" style={{ color: TEAL }}>Editing thresholds</span>
          <span class="text-text-muted">·</span>
          <span>Press <kbd class="rounded border border-border-strong px-1 font-mono text-[10px] text-text-body">Enter</kbd> or click away to save each value. You'll see <span class="font-bold text-status-ok">saved ✓</span> when it's stored.</span>
        </div>
      )}

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>}

      {items && visible.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No items match.</p>
          <p class="m-0 mt-1 text-sm text-text-secondary">Adjust your filter or search.</p>
        </div>
      )}

      {view === 'byPrinter' && groupedByPrinter.map((g) => {
        // Pull printer id from the section key 'p<id>' to prefill the add modal.
        const printerId = g.key.startsWith('p') ? Number(g.key.slice(1)) : null;
        const prefill = g.kind === 'global'
          ? { defaultCategory: 'global' }
          : { defaultPrinterId: printerId, defaultCategory: 'ink' };
        return (
          <SectionCard
            key={g.key}
            kind={g.kind}
            initial={g.initial}
            title={g.title}
            subtitle={g.subtitle}
            items={g.items}
            editing={editingThresholds}
            isAdmin={isAdmin}
            onAdjust={setAdjusting}
            onOpen={handleOpen}
            onItemUpdated={handleItemUpdated}
            onAddItem={() => openAdd(prefill)}
          />
        );
      })}

      {view === 'byCategory' && groupedByCategory.map((g) => (
        <SectionCard
          key={g.key}
          kind={g.kind}
          initial={g.initial}
          title={g.title}
          subtitle={g.subtitle}
          items={g.items}
          editing={editingThresholds}
          isAdmin={isAdmin}
          onAdjust={setAdjusting}
          onOpen={handleOpen}
          onItemUpdated={handleItemUpdated}
          onAddItem={() => openAdd({ defaultCategory: g.key })}
        />
      ))}

      <AdjustStockModal
        open={!!adjusting}
        item={adjusting}
        onClose={() => setAdjusting(null)}
        onAdjusted={() => { setAdjusting(null); refetch(); refetchStats(); }}
      />
      <NewItemModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => { setNewOpen(false); refetch(); refetchStats(); }}
        defaultPrinterId={newPrefill.defaultPrinterId}
        defaultCategory={newPrefill.defaultCategory ?? 'ink'}
      />
    </div>
  );
};
